import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Button, Card, Field, formatDateTime } from '../components/ui';
import { useToast } from '../components/Toast';

const API_BASE = process.env.REACT_APP_API_BASE || '';

/** Password and account settings, shared by the nanny and family portals. */
export default function Account() {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileRef = useRef(null);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const avatarEndpoint = `/api/${user.role}/avatar`;
  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();

  function onFileChange(event) {
    const file = event.target.files?.[0];
    setPreview(file ? URL.createObjectURL(file) : null);
  }

  async function uploadPhoto() {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error(t('account.choosePhotoFirst'));
      return;
    }
    setPhotoBusy(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const { user: updated } = await api.upload(avatarEndpoint, formData);
      setUser(updated);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
      toast.success(t('account.photoUploaded'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPhotoBusy(false);
    }
  }

  async function removePhoto() {
    if (!window.confirm(t('account.confirmRemovePhoto'))) return;
    setPhotoBusy(true);
    try {
      const { user: updated } = await api.del(avatarEndpoint);
      setUser(updated);
      toast.success(t('account.photoRemoved'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setPhotoBusy(false);
    }
  }

  async function submit(event) {
    event.preventDefault();
    if (form.newPassword !== form.confirmPassword) {
      toast.error(t('account.passwordMismatch'));
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      toast.success(t('account.passwordChanged'));
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('account.pageTitle')}</h1>
          <p className="muted">{t('account.subtitle')}</p>
        </div>
      </div>


      <Card title={t('account.profilePhoto')} subtitle={t('account.profilePhotoHint')}>
        <div className="avatar-editor">
          {preview || user.avatarUrl ? (
            <img className="avatar-preview" src={preview || `${API_BASE}${user.avatarUrl}`} alt="" />
          ) : (
            <span className="avatar-preview avatar-preview-fallback">{initials || '?'}</span>
          )}
          <div className="avatar-editor-actions">
            <input type="file" accept=".jpg,.jpeg,.png,.webp" ref={fileRef} onChange={onFileChange} />
            <div className="form-footer">
              <Button type="button" onClick={uploadPhoto} loading={photoBusy}>{t('account.uploadPhoto')}</Button>
              {user.avatarUrl && (
                <Button type="button" variant="ghost" onClick={removePhoto} loading={photoBusy}>
                  {t('account.removePhoto')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Card>

      <Card title={t('account.accountCard')}>
        <dl className="detail-grid">
          <div><dt>{t('account.name')}</dt><dd>{user.fullName}</dd></div>
          <div><dt>{t('account.email')}</dt><dd>{user.email}</dd></div>
          <div><dt>{t('account.memberSince')}</dt><dd>{formatDateTime(user.createdAt)}</dd></div>
        </dl>
        <p className="muted small">{t('account.changeElsewhere')}</p>
      </Card>

      <Card title={t('account.changePassword')}>
        <form onSubmit={submit}>
          <Field label={t('account.currentPassword')} required>
            <input type="password" value={form.currentPassword} onChange={set('currentPassword')} required />
          </Field>
          <div className="grid-2">
            <Field label={t('account.newPassword')} required hint={t('account.passwordHint')}>
              <input type="password" value={form.newPassword} onChange={set('newPassword')} minLength={8} required />
            </Field>
            <Field label={t('account.confirmNewPassword')} required>
              <input type="password" value={form.confirmPassword} onChange={set('confirmPassword')} required />
            </Field>
          </div>
          <Button type="submit" loading={busy}>{t('account.changePassword')}</Button>
        </form>
      </Card>
    </>
  );
}
